import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal } from 'antd';

import FormExposure from './FormExposure';
import { createShareLink } from '../../services/shareLinks';
import { describeIntakeError } from '../../utils/intakeLinks';
import { normalizeId } from '../../utils/ids';
import { formExposure } from '../../utils/publicForms';

/**
 * "Create link" for an invite link a package PREPARED AND LEFT OFF (keepr-api
 * KPR-360; docs/SCHEMA.md § A pack's form, prepared and off). The draft is an
 * invite link's definition — which card, what it asks, its cap and expiry —
 * and nothing anyone can reach. Before a link exists the manager reads, in
 * plain words, what it would expose (FormExposure); Create link then mints an
 * ORDINARY invite link from the draft through `POST …/share-links` — every
 * gate an intake link always had (the collection's switch on, a verified
 * session, manage) — and the one-time code is shown by the caller. The draft
 * stays, to make the next link from (one per applicant, say).
 *
 * "Change it first…" opens the intake link editor seeded with the draft
 * instead (`onChangeFirst`), for a form that should ask a little differently
 * here. While intake forms are off, Create link is disabled with the reason.
 */
export default function PreparedFormModal({
	open,
	form,
	collectionId,
	cardName = null,
	labelOf = name => name,
	disabledReason = null,
	onClose,
	onCreated,
	onChangeFirst,
}) {
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!open) return;
		setSaving(false);
		setError(null);
	}, [open, form]);

	const lines = useMemo(() => (form ? formExposure(form, { cardName, labelOf }) : []), [form, cardName, labelOf]);

	const handleCreate = async () => {
		if (!form || disabledReason) return;
		setSaving(true);
		setError(null);
		const d = form.definition || {};
		try {
			const result = await createShareLink(collectionId, {
				kind: 'intake',
				card_id: normalizeId(form.card_id),
				name: form.name || undefined,
				fields: d.fields,
				prefill: d.prefill,
				headline: d.headline,
				intro: d.intro,
				confirmation: d.confirmation,
				uploads: d.uploads,
				privateFields: d.privateFields,
				// Sent only when the draft says so, as the editor does.
				...(Array.isArray(d.shownFields) && d.shownFields.length ? { shownFields: d.shownFields } : {}),
				...(d.entryLimits && typeof d.entryLimits === 'object' && Object.keys(d.entryLimits).length ? { entryLimits: d.entryLimits } : {}),
				...(Array.isArray(d.privateRequired) ? { privateRequired: d.privateRequired } : {}),
				maxSubmissions: form.invite?.maxSubmissions,
				expiresInDays: form.invite?.expiresInDays,
			});
			onCreated?.(result, form);
		} catch (err) {
			setError(describeIntakeError(err));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			title={form?.name ? `Create a link from “${form.name}”?` : 'Create a link from this form?'}
			onCancel={onClose}
			destroyOnClose
			width={560}
			className="share-links__modal prepared-form-modal"
			footer={[
				<Button key="cancel" onClick={onClose}>Cancel</Button>,
				onChangeFirst ? <Button key="change" onClick={() => onChangeFirst(form)} data-action="prepared-form-change">Change it first…</Button> : null,
				<Button
					key="create"
					type="primary"
					loading={saving}
					disabled={Boolean(disabledReason)}
					onClick={handleCreate}
					data-action="prepared-form-create"
				>
					Create link
				</Button>,
			].filter(Boolean)}
		>
			<Alert
				className="intake-link-modal__warning"
				type="warning"
				showIcon
				message="Anyone with this link can add an item"
				description="No account, no sign-in. Read what the link will take before you make it."
			/>
			<FormExposure lines={lines} />
			{disabledReason ? <p className="share-links__hint" data-testid="prepared-form-disabled">{disabledReason}</p> : null}
			{error ? <Alert className="intake-link-modal__error" type="error" showIcon message={error} data-testid="prepared-form-error" /> : null}
		</Modal>
	);
}
